/**
 * VIP Offers Hook using TanStack Query
 * Simplified VIP offer management with automatic cache invalidation
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { VipOffer, VipOffersResource, VipPreviewResponse } from '../../core/resources/vipOffers';
import { getGlobalApiClient } from './useApiQuery';

export interface UseVipOffersQueryOptions {
    /**
     * Checkout session ID for VIP offers
     */
    sessionId?: string;

    /**
     * Array of VIP offer IDs to manage
     */
    vipOfferIds: string[];

    /**
     * Whether to automatically fetch VIP preview on mount
     * @default true
     */
    enabled?: boolean;

    /**
     * Callback when VIP offers are selected
     */
    onSelectSuccess?: () => void;

    /**
     * Callback when VIP offers are cancelled
     */
    onCancelSuccess?: () => void;
}

export interface UseVipOffersQueryResult {
    /**
     * Array of VIP offers
     */
    vipOffers: VipOffer[];

    /**
     * VIP preview data including savings and selected offers
     */
    vipPreview: VipPreviewResponse | undefined;

    /**
     * Loading state for VIP preview
     */
    isLoadingVipPreview: boolean;

    /**
     * Whether any VIP offers are currently selected
     */
    hasVip: boolean;

    /**
     * Check if a specific VIP offer is selected
     */
    isOfferSelected: (offer: VipOffer) => boolean;

    /**
     * Select all VIP offers
     */
    selectVipOffers: () => Promise<void>;

    /**
     * Cancel/deselect all VIP offers
     */
    cancelVipOffers: () => Promise<void>;

    /**
     * Refresh VIP preview data
     */
    refreshVipPreview: () => Promise<void>;

    /**
     * Loading state for select/cancel operations
     */
    isSelecting: boolean;
    isCanceling: boolean;

    /**
     * Error state
     */
    error: Error | null;
}

export function useVipOffersQuery(
    options: UseVipOffersQueryOptions
): UseVipOffersQueryResult {
    const { sessionId, vipOfferIds, enabled = true, onSelectSuccess, onCancelSuccess } = options;
    const queryClient = useQueryClient();

    // Create VIP offers resource client
    const vipOffersResource = useMemo(() => {
        try {
            return new VipOffersResource(getGlobalApiClient());
        } catch (error) {
            throw new Error(
                'Failed to initialize VIP offers resource: ' +
                (error instanceof Error ? error.message : 'Unknown error')
            );
        }
    }, []);

    // Convert offer IDs to VipOffer objects
    const vipOffers = useMemo<VipOffer[]>(() => {
        return vipOfferIds.map((id) => ({
            id,
            productId: '', // These will be populated from preview data
            variantId: '', // These will be populated from preview data
        }));
    }, [vipOfferIds]);

    // Main VIP preview query
    const {
        data: vipPreview,
        isLoading: isLoadingVipPreview,
        error: fetchError,
        refetch: refetchPreview,
    } = useQuery<VipPreviewResponse>({
        queryKey: ['vip-preview', sessionId, vipOfferIds],
        queryFn: () => vipOffersResource.getVipPreview(sessionId!, vipOfferIds),
        enabled: enabled && !!sessionId && vipOfferIds.length > 0,
        staleTime: 30_000, // 30 seconds
        refetchOnWindowFocus: false,
    });

    // Check if a specific VIP offer is selected
    const isOfferSelected = useCallback(
        (offer: VipOffer) => {
            if (!vipPreview?.selectedOffers) return false;
            return vipPreview.selectedOffers.some(
                (selected) =>
                    selected.productId === offer.productId &&
                    selected.variantId === offer.variantId &&
                    selected.isSelected
            );
        },
        [vipPreview?.selectedOffers]
    );

    // Calculate hasVip based on selected offers
    const hasVip = useMemo(() => {
        return vipPreview?.selectedOffers?.some((offer) => offer.isSelected) ?? false;
    }, [vipPreview?.selectedOffers]);

    // Toggle order bump mutation
    const toggleMutation = useMutation<
        { success: boolean; summary: any; availablePromotions: any[] },
        Error,
        { offerId: string; selected: boolean }
    >({
        mutationFn: ({ offerId, selected }) => {
            if (!sessionId) {
                throw new Error('No session ID available');
            }
            return vipOffersResource.toggleOrderBump(sessionId, offerId, selected);
        },
        onSuccess: async () => {
            // Invalidate both VIP preview and checkout queries
            if (sessionId) {
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ['vip-preview', sessionId] }),
                    queryClient.invalidateQueries({ queryKey: ['checkout'] }),
                ]);
            }
        },
    });

    // Track operation states
    const [isSelecting, setIsSelecting] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);

    // Select all VIP offers
    const selectVipOffers = useCallback(async () => {
        if (!sessionId) return;

        setIsSelecting(true);
        try {
            // Toggle all offers that aren't already selected
            const togglePromises = vipOffers
                .filter((offer) => !isOfferSelected(offer))
                .map((offer) =>
                    toggleMutation.mutateAsync({ offerId: offer.id, selected: true })
                );

            await Promise.all(togglePromises);

            // Refresh preview to get updated data
            await refetchPreview();

            // Call success callback
            if (onSelectSuccess) {
                onSelectSuccess();
            }
        } catch (error) {
            console.error('Failed to select VIP offers:', error);
            throw error;
        } finally {
            setIsSelecting(false);
        }
    }, [sessionId, vipOffers, isOfferSelected, toggleMutation, refetchPreview, onSelectSuccess]);

    // Cancel all VIP offers
    const cancelVipOffers = useCallback(async () => {
        if (!sessionId) return;

        setIsCanceling(true);
        try {
            // Toggle all offers to deselected
            const togglePromises = vipOffers.map((offer) =>
                toggleMutation.mutateAsync({ offerId: offer.id, selected: false })
            );

            await Promise.all(togglePromises);

            // Refresh preview to get updated data
            await refetchPreview();

            // Call success callback
            if (onCancelSuccess) {
                onCancelSuccess();
            }
        } catch (error) {
            console.error('Failed to cancel VIP offers:', error);
            throw error;
        } finally {
            setIsCanceling(false);
        }
    }, [sessionId, vipOffers, toggleMutation, refetchPreview, onCancelSuccess]);

    // Refresh VIP preview
    const refreshVipPreview = useCallback(async () => {
        await refetchPreview();
    }, [refetchPreview]);

    // Combine errors
    const error = (fetchError || toggleMutation.error) as Error | null;

    return {
        vipOffers,
        vipPreview,
        isLoadingVipPreview,
        hasVip,
        isOfferSelected,
        selectVipOffers,
        cancelVipOffers,
        refreshVipPreview,
        isSelecting,
        isCanceling,
        error,
    };
}


